import React, { useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import uPlot from 'uplot';
import UplotReact from 'uplot-react';
import 'uplot/dist/uPlot.min.css';
import './ForecastComparisonChartUplot.css';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectTheme } from '../slices/preferencesSlice';
import { IconState } from './IconState';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import AnimationCircle from './AnimationCircle';
import {
  CONTINUOUS_PREVIEW_COLOR,
  CONTINUOUS_PREVIEW_BAND_COLOR,
} from '../utils/continuousPreview';
import { COMPARISON_PALETTE, seriesColorAt } from '../utils/chartPalette';
import { valueAxisLabel, formatValueWithUnit } from '../utils/resourceUnits';
import { shortSourceName } from '../helpers/marketProfile';
import nowMarkerPlugin from './chart/nowMarkerPlugin';
import {
  CHART_AXIS_INK, CHART_MONO_FONT, CHART_SANS_FAMILY, CHART_SANS_FONT, CHART_SANS_PX,
} from './chart/chartFonts';
import insightsPlugin, { INSIGHTS_TOP_PAD, observedShown, ribbonAxisGap } from './chart/insightsPlugin';
import crowdLensPlugin from './chart/crowdLensPlugin';
import InsightsOverlay from './insights/InsightsOverlay';
import {
  insightTone, insightsTooltipRows, observedMarks, prepareInsights, visiblePointers,
} from './insights/prepare';

const NO_INSIGHT_OPTIONS = {};
const NO_MEDIAN = () => undefined;
// Room the insights take: a pin strip above the plot when there are pointers,
// the ribbon under it (x-axis gap) when there are insight rows, and the
// Observed strip under the ribbon when observed ramps are loaded.
const NO_LAYOUT = { pins: false, ribbon: false, observed: false };
// Turning insights on or off fades the layer and moves the room under the
// plot over this long (none when the system asks for reduced motion).
const INSIGHTS_FADE_MS = 200;
const reducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
const easeOut = (p) => 1 - (1 - p) ** 3;
// uPlot's own x-axis size and gap, kept while the insights ribbon has no data.
const X_AXIS_SIZE = 50;
const X_AXIS_GAP = 5;
// The y axis: tick values and its title. Fixed, so the plot's left edge (where
// the x axis title starts) is known before the plot exists.
const Y_AXIS_SIZE = 58;
const Y_LABEL_SIZE = 22;
// No legend entry turned off.
const NO_HIDDEN = {};

// The NOW marker's label reads hh:mm, so a repaint a minute keeps it current.
const NOW_REDRAW_MS = 60 * 1000;

// The tooltip is built as an HTML string assigned via innerHTML, and series
// labels carry user-controlled identities (forecaster username). Escape any
// value interpolated into that markup to prevent stored XSS. See audit
// A260610-FE-H1.
const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch]));

// Day-first, 24h time-axis stamp spec (mirrors uPlot's default but DD-MM and
// HH:mm). uPlot keeps its native two-tier behavior: the rollover columns add a
// second-row context (date / year) only when that unit changes. Columns:
//   [minSecs, default, year, month, day, hour, min, sec, mode(1=concat)]
const _ = null;
const Y = 86400 * 365;
const MO = 86400 * 28;
const D = 86400;
const H = 3600;
const MIN = 60;
const SEC = 1;
const DDMM = '{DD}-{MM}';
const NL_YYYY = '\n{YYYY}';
const NL_DDMM = '\n' + DDMM;
const NL_DDMMYYYY = NL_DDMM + '-{YYYY}';
const HHMM = '{HH}:{mm}';
const NL_HHMM = '\n' + HHMM;
const SS = ':{ss}';
const DAY_FIRST_TIME_STAMPS = [
  //  incr   default     year          month  day        hour  min       sec  mode
  [Y, '{YYYY}', _, _, _, _, _, _, 1],
  [MO, '{MMM}', NL_YYYY, _, _, _, _, _, 1],
  [D, DDMM, NL_YYYY, _, _, _, _, _, 1],
  [H, HHMM, NL_DDMMYYYY, _, NL_DDMM, _, _, _, 1],
  [MIN, HHMM, NL_DDMMYYYY, _, NL_DDMM, _, _, _, 1],
  [SEC, SS, NL_DDMMYYYY + ' ' + HHMM, _, NL_DDMM + ' ' + HHMM, _, NL_HHMM, _, 1],
];

// Append an alpha channel to a #RRGGBB hex (no-op for other color formats).
const withAlpha = (hex, alpha) => {
  if (typeof hex === 'string' && hex.startsWith('#') && hex.length === 7) {
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hex}${a}`;
  }
  return hex;
};

// Prototype uPlot twin of ForecastComparisonChart. Same props/data so it can
// be rendered side-by-side. uPlot is canvas, tiny, and very fast, but lower
// level: every series must share ONE x-axis, so we align all series onto a
// union timeline (null = gap). Confidence bands use uPlot's native `bands`.
export default function ForecastComparisonChartUplot({
  ensembleForecast,
  individualUserForecasts,
  rawData,
  forecasterQ50,
  timeUnit = 'day',
  quantile = 'q50',
  lowerQuantile = 'q10',
  upperQuantile = 'q90',
  loading = false,
  isForecaster = false,
  comparisonForecasts = {},
  challengeStart = null,
  challengeEnd = null,
  showUserLegend = false,
  // Forecaster lines never appear in the legend; their email is surfaced only in
  // the tooltip. When `anonymizeUsers` is true (e.g. a market owner who must not
  // learn forecaster identities), those lines still draw but are dropped from
  // the tooltip too, so no email is surfaced even on hover.
  anonymizeUsers = false,
  // When the dashboard "hide info" toggle is on, the tooltip shows each
  // forecaster's username/alias (from `userAliases`, keyed by user id) instead
  // of their email, falling back to the email when no alias exists.
  blurSensitive = false,
  userAliases = {},
  continuousPreview = [],
  horizonFilter = null,
  // Resource unit code (backend `MeasurementUnits`). Drives the value-axis
  // title and tooltip suffix; defaults to MW so power resources are unchanged.
  units = 'mw',
  // Publisher of the observed measurements (`source_name` / `source_url` on the
  // resource). Credited on the "Observed" legend entry, since the credit
  // applies to that series alone. Empty = nothing rendered, no placeholder.
  sourceName = '',
  sourceUrl = '',
  // Phone-only "Showing the ... forecast" caption over the plot. Off where the
  // host already captions the chart (the forecaster Dashboard's phone header).
  showPhoneCaption = true,
  // Replays the plot's fade-in when it changes. By default any data change
  // does; a host that adds or removes series in place (the Dashboard's
  // forecaster picker) passes a key that changes only when it wants the fade.
  animationKey,
  // Ensemble insights layer (off unless the dashboard turns it on).
  insights = null,
  insightsOn = false,
  insightsOptions = NO_INSIGHT_OPTIONS,
  // Keep the pin row above the plot whether the layer is on or off and
  // whatever its data, so the plot's top does not move. Set where the layer
  // can be used.
  insightsReserve = false,
}) {
  // ---- Theme handling (mirrors the Chart.js component) --------------------
  const theme = useSelector(selectTheme);
  const [effectiveDark, setEffectiveDark] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const compute = () =>
      setEffectiveDark(theme === 'dark' || (theme === 'system' && mq.matches));
    compute();
    const listener = () => theme === 'system' && compute();
    if (mq.addEventListener) mq.addEventListener('change', listener);
    else mq.addListener(listener);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', listener);
      else mq.removeListener(listener);
    };
  }, [theme]);

  // Insights reach the uPlot hooks through refs, so data and option changes
  // redraw without rebuilding the plot (a rebuild would lose the user's zoom).
  // insightsLayoutRef: room for the pin strip and ribbon, only with data
  // (read by uPlot); insightsLayout mirrors it for this component's render.
  const insightsRef = useRef(null);
  const insightsLayoutRef = useRef(NO_LAYOUT);
  const [insightsLayout, setInsightsLayout] = useState(NO_LAYOUT);
  const [insightsHighlight, setInsightsHighlight] = useState(null);
  const [scaleTick, setScaleTick] = useState(0);
  // Observed ramps turned off are hidden here; the loaded data keep them.
  const observedOn = insightsOptions.types?.observed !== false;
  const insightsPrep = useMemo(
    () => (insightsOn ? prepareInsights(insights, { observed: observedOn }) : null),
    [insights, insightsOn, observedOn],
  );
  // Where the layer can be used, the plot is built with it once and turning
  // insights on or off only changes what it draws: no rebuild (which flashes
  // and loses the zoom), a short fade and a moving room under the plot.
  const insightsLayer = insightsOn || insightsReserve;
  const extraRef = useRef(0); // room under the plot now (px); animated
  const alphaRef = useRef(1); // the layer's opacity now; animated
  const fadeOutRef = useRef(null); // the last insights while they fade out
  const animRef = useRef(null);
  const layerState = useCallback(() => {
    const st = insightsRef.current ?? fadeOutRef.current;
    return st && { ...st, alpha: alphaRef.current };
  }, []);
  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  // ---- Responsive sizing (uPlot needs explicit px) ------------------------
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setSize({ width: Math.floor(cr.width), height: Math.floor(cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pick = (c) => (effectiveDark ? c.dark : c.light);

  const labelFormat =
    timeUnit === 'hour'
      ? 'D MMM HH:mm'
      : timeUnit === 'month'
      ? 'MMM YYYY'
      : 'D MMM YYYY';

  // ---- Align all series onto one union timeline ---------------------------
  const { data, seriesCols, bands, hasData } = useMemo(() => {
    const safe = (d) => (Array.isArray(d) ? d : []);
    const sec = (iso) => Math.floor(moment.utc(iso).valueOf() / 1000);

    const inWindow = (iso) => {
      if (!challengeStart || !challengeEnd) return true;
      const t = moment.utc(iso);
      return t >= moment.utc(challengeStart) && t <= moment.utc(challengeEnd);
    };

    const xsSet = new Set();
    // Register a raw {datetime,value} list filtered by variable -> Map(sec->val)
    const toMap = (list, variable, applyWindow = false) => {
      const m = new Map();
      safe(list).forEach((i) => {
        if (variable && i.variable !== variable) return;
        if (!i.datetime) return;
        if (applyWindow && !inWindow(i.datetime)) return;
        const t = sec(i.datetime);
        m.set(t, i.value);
        xsSet.add(t);
      });
      return m;
    };

    // cols: ordered list of series (band edges first so lines draw on top).
    // `order` controls legend ordering only (not draw order); lower = first.
    const cols = [];
    const pushCol = (col) => cols.push(col) - 1; // returns index in cols

    const ensBandFill = effectiveDark ? 'rgba(203,213,225,0.10)' : 'rgba(169,169,169,0.25)';
    const cpBandFill = effectiveDark ? 'rgba(58,171,184,0.20)' : 'rgba(116,205,214,0.30)';

    // Ensemble band edges (collapsed into one legend entry via bandId)
    const q10Map = toMap(ensembleForecast, lowerQuantile);
    const q90Map = toMap(ensembleForecast, upperQuantile);
    let idxQ10 = -1;
    let idxQ90 = -1;
    if (q10Map.size && q90Map.size) {
      const edge = effectiveDark ? 'rgba(203,213,225,0.5)' : '#999999';
      const band = {
        kind: 'band',
        bandId: 'ensemble',
        bandLabel: `Forecast interval (${lowerQuantile}–${upperQuantile})`,
        fill: ensBandFill,
        order: 30,
      };
      idxQ10 = pushCol({ label: `${lowerQuantile} Bound`, map: q10Map, stroke: edge, width: 1, ...band });
      idxQ90 = pushCol({ label: `${upperQuantile} Bound`, map: q90Map, stroke: edge, width: 1, ...band });
    }

    // Continuous-preview band edges
    const cpRows = safe(continuousPreview).filter((r) => r.datetime);
    const cpQ10Map = toMap(cpRows, lowerQuantile);
    const cpQ90Map = toMap(cpRows, upperQuantile);
    const cpBandColor = effectiveDark
      ? CONTINUOUS_PREVIEW_BAND_COLOR.dark
      : CONTINUOUS_PREVIEW_BAND_COLOR.light;
    let idxCpQ10 = -1;
    let idxCpQ90 = -1;
    if (cpQ10Map.size && cpQ90Map.size) {
      const band = {
        kind: 'band',
        bandId: 'continuous',
        bandLabel: `Continuous band (${lowerQuantile}–${upperQuantile})`,
        fill: cpBandFill,
        order: 50,
      };
      idxCpQ10 = pushCol({ label: 'Continuous q10', map: cpQ10Map, stroke: cpBandColor, width: 1, dash: [5, 5], ...band });
      idxCpQ90 = pushCol({ label: 'Continuous q90', map: cpQ90Map, stroke: cpBandColor, width: 1, dash: [5, 5], ...band });
    }

    // Individual user forecasts (q50). Shown only when the parent opts in
    // (the dashboard does so once forecasters are selected for comparison).
    if (individualUserForecasts && typeof individualUserForecasts === 'object') {
      // Forecaster lines never appear in the legend (`hideFromLegend`). Their
      // email rides on `label` and surfaces only in the tooltip, which is itself
      // suppressed for anonymized (market-owner) viewers via `anon`.
      Object.entries(individualUserForecasts).forEach(([userId, ud], index) => {
        if (!ud || ud.error || !Array.isArray(ud.data)) return;
        const m = toMap(ud.data, null);
        if (!m.size) return;
        const color = pick(seriesColorAt(index));
        const email = ud.userName || `User ${userId.slice(0, 8)}`;
        // "Hide info" on → show the alias/username, falling back to the email.
        const ident = blurSensitive ? (userAliases[userId] || email) : email;
        const label = ident.length > 30 ? ident.slice(0, 27) + '...' : ident;
        pushCol({ label, hideFromLegend: true, map: m, stroke: color, width: 1.75, alpha: 0.9, show: showUserLegend, kind: 'line', order: 60 + index, anon: anonymizeUsers });
      });
    }

    // Comparison models (dashed)
    if (comparisonForecasts && typeof comparisonForecasts === 'object') {
      Object.entries(comparisonForecasts).forEach(([modelValue, items], i) => {
        const m = toMap(items, 'q50');
        if (!m.size) return;
        const color = pick(COMPARISON_PALETTE[i % COMPARISON_PALETTE.length]);
        pushCol({ label: modelValue, map: m, stroke: color, width: 2, dash: [6, 3], kind: 'line', order: 80 + i });
      });
    }

    // Observed
    const obsMap = toMap(rawData, null, true);
    if (obsMap.size) {
      pushCol({
        // `role` lets the legend find this entry to hang the data-source credit
        // on, without matching against the label text.
        role: 'observed',
        label: 'Observed',
        map: obsMap,
        stroke: effectiveDark ? '#e5e7eb' : '#000000',
        width: 3,
        kind: 'line',
        order: 10,
      });
    }

    // Ensemble q50
    const ensMap = toMap(ensembleForecast, quantile);
    if (ensMap.size) {
      pushCol({
        label: isForecaster ? 'Your forecast' : `Forecast (${quantile})`,
        map: ensMap,
        stroke: effectiveDark ? '#ff7b7b' : '#8B0000',
        width: 3,
        kind: 'line',
        order: 20,
      });
    }

    // Forecaster q50 (dashed)
    const fcMap = toMap(forecasterQ50, quantile);
    if (fcMap.size) {
      pushCol({
        label: isForecaster ? 'Forecast' : 'Forecaster Q50',
        map: fcMap,
        stroke: effectiveDark ? '#ffb37d' : '#FF4500',
        width: 1.5,
        dash: [5, 5],
        kind: 'line',
        order: 25,
      });
    }

    // Continuous-forecast q50 (dashed)
    const cpQ50Map = toMap(cpRows, quantile);
    if (cpQ50Map.size) {
      pushCol({
        label: 'Continuous forecast',
        map: cpQ50Map,
        stroke: effectiveDark ? CONTINUOUS_PREVIEW_COLOR.dark : CONTINUOUS_PREVIEW_COLOR.light,
        width: 2,
        dash: [5, 5],
        kind: 'line',
        order: 40,
      });
    }

    const xs = [...xsSet].sort((a, b) => a - b);
    const dataArr = [xs, ...cols.map((c) => xs.map((t) => (c.map.has(t) ? c.map.get(t) : null)))];

    // Bands reference uPlot series indices (+1 for the x series at index 0)
    const bandDefs = [];
    if (idxQ10 >= 0 && idxQ90 >= 0) {
      bandDefs.push({ series: [idxQ90 + 1, idxQ10 + 1], fill: ensBandFill });
    }
    if (idxCpQ10 >= 0 && idxCpQ90 >= 0) {
      bandDefs.push({ series: [idxCpQ90 + 1, idxCpQ10 + 1], fill: cpBandFill });
    }

    return { data: dataArr, seriesCols: cols, bands: bandDefs, hasData: xs.length > 0 && cols.length > 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ensembleForecast,
    individualUserForecasts,
    rawData,
    forecasterQ50,
    comparisonForecasts,
    continuousPreview,
    quantile,
    lowerQuantile,
    upperQuantile,
    effectiveDark,
    isForecaster,
    showUserLegend,
    anonymizeUsers,
    blurSensitive,
    userAliases,
    challengeStart,
    challengeEnd,
  ]);

  const options = useMemo(() => {
    const axisColor = effectiveDark ? CHART_AXIS_INK.dark : CHART_AXIS_INK.light;
    const gridColor = effectiveDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const spline = uPlot.paths.spline ? uPlot.paths.spline() : undefined;

    const tooltipBg = effectiveDark ? '#0b1220' : '#ffffff';

    // Floating tooltip: an absolutely-positioned overlay (so it never reflows
    // the layout like the live legend did). Lists each visible series' value at
    // the cursor, with the UTC datetime as the header.
    const tooltipPlugin = {
      hooks: {
        init: (u) => {
          const tt = document.createElement('div');
          Object.assign(tt.style, {
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: '100',
            padding: '6px 8px',
            font: CHART_MONO_FONT,
            borderRadius: '6px',
            border: `1px solid ${gridColor}`,
            background: tooltipBg,
            color: axisColor,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'none',
            whiteSpace: 'nowrap',
          });
          u.over.appendChild(tt);
          u._tooltip = tt;
        },
        setCursor: (u) => {
          const tt = u._tooltip;
          if (!tt) return;
          const { idx, left, top } = u.cursor;
          if (idx == null || left == null || left < 0 || top == null || top < 0) {
            tt.style.display = 'none';
            return;
          }
          const ts = u.data[0][idx];
          const header = moment.utc(ts * 1000).format('D MMM YYYY, HH:mm [UTC]');
          // Sorted by value (desc) so rows mirror the vertical order of the
          // lines at the cursor.
          const entries = [];
          for (let i = 1; i < u.series.length; i++) {
            const s = u.series[i];
            if (s.show === false) continue;
            if (s._anon) continue; // hide anonymized forecaster identities
            const val = u.data[i][idx];
            if (val == null) continue;
            const color = typeof s.stroke === 'function' ? s.stroke(u, i) : s.stroke;
            entries.push({ label: s.label, val, color });
          }
          entries.sort((a, b) => b.val - a.val);
          const rows = entries
            .map(
              (e) =>
                `<div style="display:flex;align-items:center;gap:6px">` +
                `<span style="width:10px;height:10px;border-radius:2px;background:${e.color};display:inline-block"></span>` +
                `<span style="flex:1;padding-right:8px">${escapeHtml(e.label)}</span>` +
                `<span style="font-weight:600">${escapeHtml(formatValueWithUnit(e.val, units))}</span>` +
                `</div>`,
            )
            .join('');
          const insightRows = insightsTooltipRows(insightsRef.current?.prep, ts, insightsRef.current?.types)
            .map((r) =>
              `<div style="display:flex;align-items:center;gap:6px">`
              + `<span style="width:10px;height:2px;background:${insightTone(r.kind, effectiveDark)};display:inline-block"></span>`
              + `<span style="flex:1;padding-right:8px">${escapeHtml(r.label)}</span>`
              + `<span style="font-weight:600">${escapeHtml(r.value)}</span>`
              + `</div>`)
            .join('');
          tt.innerHTML = `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(header)}</div>${rows}${insightRows}`;
          tt.style.display = 'block';
          const ow = u.over.clientWidth;
          const oh = u.over.clientHeight;
          let x = left + 14;
          let y = top + 14;
          if (x + tt.offsetWidth > ow) x = left - tt.offsetWidth - 14;
          if (y + tt.offsetHeight > oh) y = top - tt.offsetHeight - 14;
          tt.style.left = `${Math.max(0, x)}px`;
          tt.style.top = `${Math.max(0, y)}px`;
        },
      },
    };

    const onSetScale = (u, scaleKey) => {
      if (scaleKey !== 'x') return;
      const btn = resetBtnRef.current;
      if (!btn) return;
      const xs = u.data && u.data[0];
      if (!xs || !xs.length) {
        btn.style.display = 'none';
        return;
      }
      const dataMin = xs[0];
      const dataMax = xs[xs.length - 1];
      const eps = (dataMax - dataMin) * 0.0001;
      const visMin = u.scales.x.min;
      const visMax = u.scales.x.max;
      const zoomed = visMin > dataMin + eps || visMax < dataMax - eps;
      btn.style.display = zoomed ? 'inline-flex' : 'none';
    };

    // Insights on: the pin strip follows zoom and pan, and the top padding and
    // x-axis size and gap follow insightsLayoutRef (set before the plot is
    // created; later changes re-run uPlot's layout with setSize, no rebuild).
    const withInsightsTick = (u, scaleKey) => {
      onSetScale(u, scaleKey);
      if (scaleKey === 'x') setScaleTick((k) => k + 1);
    };
    // The pin row above the plot is kept when reserved, so the plot's top does
    // not move when the insights come or go; the rows under it follow the data
    // (kept empty they would open a gap between the plot and its tick labels).
    const insightsPad = () => (insightsReserve || insightsLayoutRef.current.pins ? INSIGHTS_TOP_PAD : 12);
    // The x axis title under the plot starts where the plot does.
    const syncPlotLeft = (u) => setPlotLeft(u.bbox.left / (uPlot.pxRatio || 1));
    // uPlot applies a new size or top padding in a deferred commit and then
    // fires setSize: the pins (placed from the plot's rect) are placed again.
    const withInsightsLayout = (u) => {
      syncPlotLeft(u);
      setScaleTick((k) => k + 1);
    };
    const insightsAxisSize = () => X_AXIS_SIZE + extraRef.current;
    const insightsAxisGap = (u) => {
      u.axes[0].gap = Math.max(X_AXIS_GAP, extraRef.current);
    };
    const hooks = insightsLayer
      ? { init: [insightsAxisGap], setScale: [withInsightsTick], setSize: [withInsightsLayout] }
      : { setScale: [onSetScale], setSize: [syncPlotLeft] };

    return {
      plugins: [
        tooltipPlugin,
        nowMarkerPlugin({ dark: effectiveDark }),
        ...(insightsLayer
          ? [insightsPlugin({ getState: layerState }),
            crowdLensPlugin({ getState: () => insightsRef.current })]
          : []),
      ],
      hooks,
      width: size.width || 600,
      // Floor only guards degenerate first-paint measurements; keep it low
      // enough that a phone-landscape container (~140px after chrome) still
      // fits without the canvas overflowing onto the legend below it.
      height: size.height ? Math.max(size.height, 120) : 320,
      padding: insightsLayer ? [insightsPad, 12, 0, 0] : [12, 12, 0, 0],
      // No focus.prox: hovering should not dim/recolor the other series.
      // Left-drag = zoom to region; Shift-drag or middle-drag = pan sideways;
      // double-click = reset to full range.
      cursor: {
        drag: { x: true, y: false },
        bind: {
          mousedown: (u, targ, handle, onlyTarg = true) => (e) => {
            if (e.shiftKey || e.button === 1) {
              e.preventDefault();
              const startX = e.clientX;
              const min0 = u.scales.x.min;
              const max0 = u.scales.x.max;
              const valPerPx = (max0 - min0) / (u.over.clientWidth || 1);
              const xs = u.data[0];
              const dataMin = xs[0];
              const dataMax = xs[xs.length - 1];
              const onMove = (ev) => {
                const shift = (ev.clientX - startX) * valPerPx;
                let nmin = min0 - shift;
                let nmax = max0 - shift;
                const w = nmax - nmin;
                if (nmin < dataMin) { nmin = dataMin; nmax = dataMin + w; }
                if (nmax > dataMax) { nmax = dataMax; nmin = dataMax - w; }
                u.setScale('x', { min: nmin, max: nmax });
              };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
              return; // skip uPlot's zoom-select for the pan gesture
            }
            // Default uPlot behavior (left-button drag-zoom), mirrors filtBtn0.
            e.button === 0 && (!onlyTarg || e.target === targ) && handle(e);
          },
        },
      },
      // Native legend disabled; a custom HTML legend is rendered below (it
      // collapses band-edge pairs, shows line/dash/band swatches, and toggles).
      legend: { show: false },
      // Render the time axis in UTC so labels match the data (and the other
      // charts) regardless of the viewer's local timezone.
      tzDate: (ts) => uPlot.tzDate(new Date(ts * 1000), 'UTC'),
      // X auto-fits the full queried range (the date picker is the source of
      // truth). Drag-select zooms in; double-click resets to the full range.
      scales: {
        x: { time: true },
        // Anchor the baseline at 0 for positive-only data, but extend the
        // lower bound to the data min when negative values are present.
        y: { range: (u, min, max) => [min < 0 ? min : 0, max] },
      },
      bands,
      series: [
        {
          label: 'Datetime',
          value: (u, ts) => (ts == null ? '' : moment.utc(ts * 1000).format(labelFormat)),
        },
        ...seriesCols.map((c) => ({
          label: c.label,
          stroke: c.alpha ? withAlpha(c.stroke, c.alpha) : c.stroke,
          width: c.width ?? 1.5,
          dash: c.dash,
          paths: spline,
          // Bridge the nulls introduced by aligning every series onto one
          // shared timeline, so the spline stays continuous and smooth.
          spanGaps: true,
          points: { show: false },
          show: c.show !== false,
          _anon: c.anon === true,
          value: (u, v) => formatValueWithUnit(v, units),
        })),
      ],
      axes: [
        {
          // Native two-tier UTC time axis (via tzDate), day-first DD-MM / 24h.
          // Its title sits in the row under the plot (with the legend), so the
          // canvas keeps no room for it.
          font: CHART_MONO_FONT,
          stroke: axisColor,
          grid: { stroke: gridColor, width: 1 },
          ticks: { stroke: gridColor, width: 1 },
          values: DAY_FIRST_TIME_STAMPS,
          ...(insightsLayer ? { size: insightsAxisSize } : {}),
        },
        {
          label: valueAxisLabel(units),
          labelSize: Y_LABEL_SIZE,
          labelFont: CHART_SANS_FONT,
          font: CHART_MONO_FONT,
          stroke: axisColor,
          grid: { stroke: gridColor, width: 1 },
          ticks: { stroke: gridColor, width: 1 },
          size: Y_AXIS_SIZE,
          values: (u, splits) => splits.map((v) => v.toLocaleString()),
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.width, size.height, effectiveDark, seriesCols, bands, labelFormat, units, insightsLayer, insightsReserve]);

  // Signature that changes when the underlying data changes — used to replay
  // the fade-in (mirrors Chart.js's draw-in feel) and re-apply the default window.
  const dataSig = useMemo(() => {
    const xs = data?.[0];
    return xs && xs.length
      ? `${seriesCols.length}:${xs.length}:${xs[0]}:${xs[xs.length - 1]}`
      : 'empty';
  }, [data, seriesCols.length]);

  // ---- Custom legend ------------------------------------------------------
  // Built from seriesCols: band-edge pairs collapse to a single entry, items
  // are ordered by `order`, and each carries the uPlot series index/indices it
  // toggles. Visibility is driven via the uPlot instance captured on create.
  const uplotRef = useRef(null);
  // Legend entries turned off, for one dataset: a new dataset (series indices
  // may shift) starts with every entry on, already in the render that builds
  // its plot, so onCreate never hides a series of the new data.
  const [hiddenState, setHiddenState] = useState({ sig: null, ids: NO_HIDDEN });
  const hidden = hiddenState.sig === dataSig ? hiddenState.ids : NO_HIDDEN;
  // Zoom-state is tracked WITHOUT React state on purpose: the reset button is
  // toggled via direct DOM style from the uPlot setScale hook. Re-rendering
  // React mid-interaction is what we must avoid - any parent-driven re-render
  // recreates the uPlot instance (options identity changes) and silently
  // discards the zoom the user just made.
  const resetBtnRef = useRef(null);
  // The plot's left edge in CSS px (after the y axis), for the x axis title;
  // the y axis width until uPlot reports its rect.
  const [plotLeft, setPlotLeft] = useState(Y_AXIS_SIZE + Y_LABEL_SIZE);

  // Layout effect: runs before uplot-react creates a plot, so a new plot reads
  // the current insights at its first draw.
  const getPlot = useCallback(() => uplotRef.current, []);
  useLayoutEffect(() => {
    const prevState = insightsRef.current;
    insightsRef.current = insightsPrep
      ? {
        prep: insightsPrep,
        dark: effectiveDark,
        highlight: insightsHighlight,
        types: insightsOptions.types || {},
        strongOnly: insightsOptions.strongOnly !== false,
        showPast: Boolean(insightsOptions.showPast),
        cutoffSec: insightsOptions.pastCutoff ?? undefined,
      }
      : null;
    const layout = insightsPrep
      ? {
        pins: insightsPrep.pointers.length > 0,
        ribbon: insightsPrep.t.length > 0,
        // The Observed strip sits under the ribbon: room for it only with both.
        observed: insightsPrep.t.length > 0 && observedShown({ prep: insightsPrep, types: insightsOptions.types }),
      }
      : NO_LAYOUT;
    const prev = insightsLayoutRef.current;
    // Without a reserved row the top padding follows the pins: re-run the layout.
    let padPending = layout.pins !== prev.pins && !insightsReserve;
    if (layout.pins !== prev.pins || layout.ribbon !== prev.ribbon || layout.observed !== prev.observed) {
      insightsLayoutRef.current = layout;
      setInsightsLayout(layout);
    }
    const extra = layout.ribbon ? ribbonAxisGap(layout.observed) : 0;
    const u = uplotRef.current;
    if (!u || !insightsLayer) {
      // No plot yet (it reads the room at creation) or no layer: no animation.
      cancelAnimationFrame(animRef.current);
      extraRef.current = extra;
      alphaRef.current = 1;
      fadeOutRef.current = null;
      return;
    }
    let fade = null;
    if (!prevState && insightsRef.current) fade = 'in';
    if (prevState && !insightsRef.current) {
      fade = 'out';
      fadeOutRef.current = prevState;
    }
    const fromExtra = extraRef.current;
    if (!fade && extra === fromExtra) {
      if (padPending) {
        u.setSize({ width: u.width, height: u.height });
        setScaleTick((k) => k + 1);
      } else {
        u.redraw(false, false);
      }
      return;
    }
    // One step: the room under the plot (re-runs the layout when it moves;
    // the setSize hook places the pins again) and the layer's opacity.
    const apply = (k) => {
      const next = fromExtra + (extra - fromExtra) * k;
      if (fade) alphaRef.current = fade === 'in' ? k : 1 - k;
      if (next !== extraRef.current || padPending) {
        padPending = false;
        extraRef.current = next;
        u.axes[0].gap = Math.max(X_AXIS_GAP, next);
        u.setSize({ width: u.width, height: u.height });
      } else {
        u.redraw(false, false);
      }
    };
    const finish = () => {
      apply(1);
      alphaRef.current = 1;
      if (fade === 'out') {
        fadeOutRef.current = null;
        u.redraw(false, false);
      }
      setScaleTick((k) => k + 1);
    };
    cancelAnimationFrame(animRef.current);
    if (reducedMotion() || typeof requestAnimationFrame !== 'function') {
      finish();
      return;
    }
    let t0 = null; // the first frame's time: the clock is the frames' own
    const step = (now) => {
      if (uplotRef.current !== u) return; // rebuilt meanwhile: the new plot read the room
      if (t0 == null) t0 = now;
      const p = Math.min(1, (now - t0) / INSIGHTS_FADE_MS);
      if (p >= 1) {
        finish();
        return;
      }
      apply(easeOut(p));
      animRef.current = requestAnimationFrame(step);
    };
    apply(0);
    animRef.current = requestAnimationFrame(step);
  }, [insightsLayer, insightsReserve, insightsPrep, effectiveDark, insightsHighlight, insightsOptions]);
  const shownPointers = useMemo(
    () => (insightsPrep
      ? visiblePointers(insightsPrep.pointers, {
        types: insightsOptions.types || {},
        strongOnly: insightsOptions.strongOnly !== false,
        showPast: Boolean(insightsOptions.showPast),
        cutoffSec: insightsOptions.pastCutoff ?? undefined,
      })
      : []),
    [insightsPrep, insightsOptions],
  );
  // The Observed strip's marks, each with whether a shown warning covered it.
  const observedBlocks = useMemo(
    () => (insightsPrep?.observed ? observedMarks(insightsPrep, insightsOptions.types) : null),
    [insightsPrep, insightsOptions.types],
  );
  const ensembleQ50At = useMemo(() => {
    if (!insightsOn) return NO_MEDIAN;
    const bySec = new Map();
    (ensembleForecast || []).forEach((r) => {
      if ((r.variable ?? quantile) === quantile) bySec.set(Date.parse(r.datetime) / 1000, r.value);
    });
    return (sec) => bySec.get(sec);
  }, [insightsOn, ensembleForecast, quantile]);

  const legendItems = useMemo(() => {
    const items = [];
    const byBand = new Map();
    seriesCols.forEach((c, i) => {
      const seriesIdx = i + 1; // x-series occupies index 0
      if (c.hideFromLegend) return; // forecaster lines draw but carry no legend entry
      if (c.kind === 'band') {
        const existing = byBand.get(c.bandId);
        if (existing) {
          existing.seriesIdx.push(seriesIdx);
          return;
        }
        const item = {
          id: `band:${c.bandId}`,
          kind: 'band',
          label: c.bandLabel || c.label,
          fill: c.fill,
          edge: c.stroke,
          dash: c.dash,
          order: c.order ?? 100,
          seriesIdx: [seriesIdx],
        };
        byBand.set(c.bandId, item);
        items.push(item);
      } else {
        items.push({
          id: `line:${i}`,
          kind: 'line',
          label: c.label,
          color: c.alpha ? withAlpha(c.stroke, c.alpha) : c.stroke,
          dash: c.dash,
          width: c.width ?? 1.5,
          order: c.order ?? 100,
          seriesIdx: [seriesIdx],
          // Only the observed series is credited: the forecasts are produced on
          // the platform, not published by the measurement source.
          ...(c.role === 'observed' && sourceUrl && sourceName
            ? {sourceName, sourceShort: shortSourceName(sourceName), sourceUrl}
            : {}),
        });
      }
    });
    return items.sort((a, b) => a.order - b.order);
  }, [seriesCols, sourceName, sourceUrl]);

  // Hide the zoom-reset affordance whenever the dataset changes (the recreated
  // chart starts at full range); the legend toggles reset with `hidden`.
  useEffect(() => {
    if (resetBtnRef.current) resetBtnRef.current.style.display = 'none';
  }, [dataSig]);

  // Keep the NOW marker moving: repaint from the cached paths (no rebuild,
  // no rescale), so the plot and any zoom stay as they are.
  useEffect(() => {
    const tick = setInterval(() => uplotRef.current?.redraw(false, false), NOW_REDRAW_MS);
    return () => clearInterval(tick);
  }, []);

  const toggleItem = (item) => {
    const next = !hidden[item.id];
    const u = uplotRef.current;
    if (u) item.seriesIdx.forEach((i) => u.setSeries(i, { show: !next }));
    setHiddenState({ sig: dataSig, ids: { ...hidden, [item.id]: next } });
  };

  const renderSwatch = (item) => {
    if (item.kind === 'band') {
      return (
        <span
          aria-hidden="true"
          className="inline-block rounded-[3px] shrink-0"
          style={{ width: 22, height: 12, background: item.fill, border: `1px solid ${item.edge}` }}
        />
      );
    }
    return (
      <svg width="22" height="12" className="shrink-0" aria-hidden="true">
        <line
          x1="1"
          y1="6"
          x2="21"
          y2="6"
          stroke={item.color}
          strokeWidth={Math.min(item.width || 1.5, 3)}
          strokeDasharray={item.dash ? item.dash.join(',') : undefined}
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const horizonKeyword =
    horizonFilter === 'latest'
      ? 'latest'
      : Number(horizonFilter) === 0 ? 'intraday' : `D+${horizonFilter}`;

  return (
    // No top padding on desktop: the pin row (or the plot) starts right under
    // the card header. Phones keep it for the "Showing the ... forecast" caption.
    <div className="relative bg-surface dark:bg-gray-870 w-full h-full px-2 pb-2 pt-2 lg:pt-0 uplot-host flex flex-col">
      {showPhoneCaption && horizonFilter != null && (
        <span
          className="lg:hidden absolute top-2 right-3 z-10 text-[11px] text-gray-600 dark:text-gray-300 pointer-events-none select-none"
          title="What the chart currently displays, based on the selected horizon filter"
        >
          {horizonFilter === 'latest' ? (
            <>
              Showing the{' '}
              <span className="font-semibold italic text-brand-600 dark:text-brand-400">{horizonKeyword}</span>
              {' '}forecast for each time
            </>
          ) : (
            <>
              Showing{' '}
              <span className="font-semibold italic text-brand-600 dark:text-brand-400">{horizonKeyword}</span>
              -horizon forecasts
            </>
          )}
        </span>
      )}
      <div ref={containerRef} className="relative w-full flex-1 min-h-0">
        {loading ? (
          <div className="h-full animate-fadeIn">
            <AnimationCircle />
          </div>
        ) : !hasData ? (
          <IconState
            icon={ExclamationCircleIcon}
            description="No observed or forecast data available for the selected period"
            title="Chart unavailable"
          />
        ) : size.width > 0 ? (
          <div key={animationKey ?? dataSig} className={`${insightsLayer ? 'relative ' : ''}h-full animate-fadeIn`}>
            <UplotReact
              options={options}
              data={data}
              onCreate={(u) => {
                uplotRef.current = u;
                setPlotLeft(u.bbox.left / (uPlot.pxRatio || 1));
                // A rebuilt plot (resize, theme, insights) draws every series:
                // hide again what the legend has off, so both agree.
                legendItems.forEach((it) => {
                  if (hidden[it.id]) it.seriesIdx.forEach((i) => u.setSeries(i, { show: false }));
                });
                if (insightsLayer) setScaleTick((k) => k + 1);
              }}
            />
            {insightsOn && insightsPrep && (
              <InsightsOverlay
                getPlot={getPlot}
                tick={scaleTick}
                pointers={shownPointers}
                params={insightsPrep.params}
                medianAt={ensembleQ50At}
                onHighlight={setInsightsHighlight}
                observed={insightsLayout.observed ? observedBlocks : null}
                onAbout={insightsOptions.onAbout}
              />
            )}
          </div>
        ) : null}
        <button
          ref={resetBtnRef}
          type="button"
          // Below the insights pin strip when it shows, so it never covers a pin.
          style={{ display: 'none', ...(insightsReserve || insightsLayout.pins ? { top: INSIGHTS_TOP_PAD + 4 } : {}) }}
          onClick={() => {
            const u = uplotRef.current;
            if (!u || !u.data?.[0]?.length) return;
            u.setScale('x', { min: u.data[0][0], max: u.data[0][u.data[0].length - 1] });
          }}
          className="absolute right-2 top-2 z-10 items-center gap-1 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-white/90 dark:bg-gray-700/90 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm backdrop-blur-sm transition-colors"
        >
          Reset zoom
        </button>
      </div>
      {!loading && hasData && (
        /* Under the tick labels, one line: the time axis title on the left and
           the legend on the right, right-aligned. On narrow cards and phones
           the legend drops under the title, still right-aligned. Shown on every
           width: the legend also carries the data-source credit. */
        <div
          data-testid="chart-footer"
          className="flex flex-wrap items-baseline gap-x-6 gap-y-1.5 pr-3 pt-1.5 select-none"
        >
          {/* As the y axis title on the canvas: same font, size and ink. */}
          <p
            className="whitespace-nowrap font-normal"
            style={{
              marginLeft: plotLeft,
              fontFamily: CHART_SANS_FAMILY,
              fontSize: CHART_SANS_PX,
              color: effectiveDark ? CHART_AXIS_INK.dark : CHART_AXIS_INK.light,
            }}
          >
            Datetime (UTC)
          </p>
          <div data-testid="chart-legend" className="ml-auto flex min-w-0 justify-end">
            {legendItems.length > 0 && (
              <ul className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5 sm:gap-x-5 text-[12px] text-gray-600 dark:text-gray-300">
                {legendItems.map((item) => {
                  const off = !!hidden[item.id];
                  return (
                    <li key={item.id} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        aria-pressed={!off}
                        onClick={() => toggleItem(item)}
                        title={off ? `Show ${item.label}` : `Hide ${item.label}`}
                        className={`flex items-center gap-1.5 rounded transition-opacity hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ring-offset-surface dark:ring-offset-gray-870 ${
                          off ? 'opacity-40' : 'opacity-100'
                        }`}
                      >
                        {renderSwatch(item)}
                        <span className={off ? 'line-through' : ''}>{item.label}</span>
                      </button>
                      {item.sourceUrl && (
                        /* Data-source credit, next to the toggle (not inside it).
                           Shortened to one word to keep the legend on one row;
                           the full publisher name rides in the title. */
                        <span className="whitespace-nowrap">
                          (<a
                            href={item.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Observed data published by ${item.sourceName}`}
                            className="text-brand-600 hover:underline underline-offset-2 dark:text-brand-400"
                          >
                            {item.sourceShort} ↗
                          </a>)
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
